import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { NAV_ICONS } from '@pages/App/components/Nav/navIcons';
import { cloneElement, isValidElement, ReactNode } from 'react';
import { VerbId } from './ids';

/**
 * The menu/drawer size of a glyph, derived from the 24px one rather than rebuilt.
 *
 * Deriving matters: `navIcons.tsx` calls itself "single source of truth … so a change to a nav
 * icon (say, the Agents sparkles) updates everywhere at once". Constructing `iconSmall` from a
 * direct MUI import instead would have meant a nav glyph swap updating the rails and leaving
 * every kebab and drawer on the old icon — the exact drift this module exists to end.
 *
 * `fontSize` only applies to MUI icons; our own SVG components (e.g. `SearchIcon`) take no props
 * and hardcode their size, so cloning is a harmless no-op there and the CSS sizes them instead.
 */
const smallOf = (icon: ReactNode): ReactNode =>
  isValidElement(icon)
    ? cloneElement(icon as React.ReactElement<{ fontSize?: string }>, { fontSize: 'small' })
    : icon;

/**
 * The verbs, in one place.
 *
 * A "verb" is something the user does TO a library or a media item — the actions that keep
 * reappearing on new surfaces: rails on cards and table rows, labelled pills in the two
 * headers, kebab menus, mobile drawers, workspace signposts. Behaviour already had a home
 * (`useLibraryVerbs` for the library-scoped pair, `useMediaMenuActions.executeAction` for the
 * media-scoped ones); *identity* did not, so every surface hand-copied the label and the
 * glyph. By the time this file was written there were eight independent spellings of "Work
 * with an Agent", seven of "Find moments", eight separate `AutoAwesomeIcon` literals and two
 * different ids for the same verb — and the drift was real, not theoretical: one review found
 * two different glyphs for "Find moments", and renaming the clip verb had to be done at
 * three sites.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS MODULE MUST STAY PURE DATA. No hooks, no MobX context, no store imports.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * That is a hard constraint, not a style preference. `useMediaAdminActions` calls
 * `useLibraryFolder()` and `useMediaDeleteModal()`, and `MediaDeleteModalProvider` is mounted
 * in exactly one place (`LibraryPage.tsx`) — so anything that reaches those hooks white-screens
 * `/search` and `/atlas`. Keeping labels and glyphs in a hook-free module is what lets a search
 * or Atlas surface name a verb without mounting the machinery that runs it.
 *
 * The behaviour side has since been split the same way: `useMediaVerbs` (`@lib/media`) is the
 * context-free half and runs anywhere, `useMediaAdminActions` is the provider-bound half and
 * does not. `MomentModal` no longer gates its kebab on `!isSearchResult` — it picks a different
 * *component* per variant instead, which is the only legal way to make that choice.
 *
 * Do not add a `useVerbs()` hook here.
 *
 * WHAT THIS FILE DELIBERATELY DOES NOT OWN:
 *
 * - **Order.** Each surface hardcodes its own, and they differ on purpose: the card rail puts
 *   Add media *between* the two verbs so the row reads work → fill → find, while the mobile
 *   drawer leads with the verbs and follows with housekeeping. Centralising the order would
 *   force one of those to be wrong.
 * - **Gating.** Empty / read-only libraries, `status === 'completed'`, the `CHAT_URL` and
 *   image checks on clipping — all stay where the context that decides them actually is.
 *   This file supplies the WORDS for those states (see the reason builders at the bottom); it
 *   never decides when they apply.
 * - **The moment-scoped agent verb** (`'agent-on-clip'` in `MomentModal`). Its label names a
 *   clip rather than a library or a media item, and the moment family has its own tracking
 *   events. Out of scope until that family is unified.
 * - **Workspace signpost CTA labels, and the Podcasts page's "Clip as a podcast" button.**
 *   The signposts read as first-run copy ("Clip with AI", "Search this library") rather than
 *   verb names, and are meant to be free to differ; the Podcasts button starts a project with
 *   no media picked, which the clip verb's "Clip this media" would misname. Both take their
 *   *glyphs* from here; their words stay local.
 * - **The analytics wire values.** Those live in `./ids`, which imports nothing, so stores can
 *   read the id vocabulary without pulling this file's glyphs (and `@pages`) in behind it.
 */

interface VerbDescriptor {
  /**
   * The one true label. Menus, tooltips and accessible names all read this, except the surfaces
   * listed under WHAT THIS FILE DELIBERATELY DOES NOT OWN.
   */
  label: string;
  /** Default 24px glyph — rails and labelled pills. */
  icon: ReactNode;
  /**
   * Whether this verb normally carries the purple. Surfaces override: on an empty library the
   * accent moves to Add media, since that's the only thing worth doing there. DESIGN.md's One
   * Accent Rule caps purple at ≤10% of a screen, so at most ONE item per cluster may take it —
   * two accented glyphs in one rail is no accent.
   *
   * Required, and spelled out even when `false`, so adding a verb forces an answer rather than
   * defaulting into "not the accent" silently.
   */
  defaultAccent: boolean;
  /**
   * The outcome line, shown as a tooltip on labelled surfaces where the label already names
   * the action. Two variants because the same verb reads differently depending on whether the
   * surface has already named its target: `scoped` for a page that IS one library ("this
   * library"), `unscoped` for a page that is every library ("a library").
   */
  outcome?: { scoped: string; unscoped: string };
  /**
   * The outcome line on a surface about ONE media — the moment modal's pills. Its own field
   * because `outcome`'s two variants are about which library is in view, a question a single
   * media doesn't raise. Only the verbs that can become a pill there carry one; read it through
   * `mediaOutcome`.
   */
  mediaOutcome?: string;
  /**
   * `mediaOutcome` for a pill that sits beside an agent CTA narrowed to a range this verb does
   * NOT take — two pills side by side read as acting on the same thing unless this one says
   * otherwise. Given the range's label ("0:42 – 1:05"). A verb that ignores the range and lacks
   * this falls back to its plain `mediaOutcome`, which then stays silent about the range.
   */
  mediaOutcomeIgnoringRange?: (rangeLabel: string) => string;
}

/**
 * Glyphs come from `NAV_ICONS` wherever the nav has an equivalent, so an icon-only rail is
 * legible: it echoes something the user has already seen labelled in the sidebar. That rule
 * predates this file (it's why "Find moments" uses the sidebar's Moments glyph rather than
 * MUI's `SearchRounded`), but it was only ever half-applied — the agent verb built its own
 * `AutoAwesomeIcon` instead of reading `NAV_ICONS.agents`, and clipping used a scissors that
 * echoed nothing at all. Reading all of them from here makes the rule uniform.
 *
 * `add-media` is the one verb with no nav equivalent — there is no "Add" nav row — so it keeps
 * a plain MUI glyph.
 */
export const VERBS = {
  'work-with-agent': {
    // "Chat" names what the click produces, matching the outcome lines below; "Agent" keeps the
    // echo of the sidebar's Agents row.
    label: 'Chat with an Agent',
    icon: NAV_ICONS.agents,
    defaultAccent: true,
    outcome: {
      scoped: 'Opens a new chat about this library',
      unscoped: 'Opens a new chat about a library',
    },
    mediaOutcome: 'Opens a new chat about this media',
  },

  'find-moments': {
    label: 'Find moments',
    icon: NAV_ICONS.search,
    defaultAccent: false,
    outcome: {
      scoped: "Search clips by what's shown or said",
      unscoped: "Search a library's clips by what's shown or said",
    },
  },

  'add-media': {
    label: 'Add media',
    icon: <AddRoundedIcon />,
    // Not accented by default — but `buildLibraryRailItems` overrides it to `true` on an empty
    // library, where this becomes the only thing worth doing.
    defaultAccent: false,
  },

  'clip-podcast': {
    // Names the media rather than the format: outside a podcast library the Clipper sits beside
    // the Agent as a secondary pill, where "as a podcast" read as the wrong tool for a talk or a
    // keynote. The id stays `clip-podcast` because it is the analytics wire value.
    label: 'Clip this media',
    defaultAccent: false,
    // The sidebar's Podcasts glyph, replacing a scissors that echoed nothing. The scissors was
    // the more literal picture of "clip", but it was the only verb glyph with no labelled
    // counterpart anywhere in the app — and it now belongs solely to the moment modal's
    // trim-timeline toggle, which is a different capability.
    icon: NAV_ICONS.podcasts,
    mediaOutcome: 'Opens the Podcast Clipper to make short clips from this media',
    // Clipping always takes the whole media, whatever range the agent pill beside it carries.
    mediaOutcomeIgnoringRange: (rangeLabel) =>
      `Opens the Podcast Clipper to make short clips from the whole media, not just ${rangeLabel}`,
  },
} satisfies Record<VerbId, VerbDescriptor>;

/**
 * The verbs that have an outcome line, DERIVED from the catalog above rather than hand-listed —
 * so adding or removing an `outcome` updates who may be passed to `verbPillItem` automatically,
 * and can't leave a labelled surface with a tooltip-less pill.
 *
 * This is why `VERBS` is `satisfies Record<…>` rather than annotated `: Record<…>`: the
 * annotation would flatten every entry to `outcome?: …` and make the distinction unknowable.
 */
type VerbsWithOutcome = {
  [K in VerbId]: (typeof VERBS)[K] extends { outcome: unknown } ? K : never;
}[VerbId];

/**
 * A verb's outcome line for a single-media surface, or `undefined` for a verb without one.
 * Pass `ignoredRangeLabel` when the pill sits beside an agent CTA narrowed to that range — see
 * `mediaOutcomeIgnoringRange`.
 *
 * Widened through the annotation rather than read off `VERBS[id]` directly: `satisfies` keeps
 * each entry's literal shape, so on a verb with no `mediaOutcome` the property doesn't exist
 * and indexing by a `VerbId` union won't compile.
 */
export const mediaOutcome = (id: VerbId, ignoredRangeLabel?: string): string | undefined => {
  const descriptor: VerbDescriptor = VERBS[id];
  if (ignoredRangeLabel !== undefined && descriptor.mediaOutcomeIgnoringRange) {
    return descriptor.mediaOutcomeIgnoringRange(ignoredRangeLabel);
  }
  return descriptor.mediaOutcome;
};

/**
 * The identity half of a `VerbRailItem`. Spread it, then add whatever the surface owns —
 * `onClick`, `hidden`, `disabled`, an accent override:
 *
 *     { ...verbItem('add-media'), accent: isEmpty, onClick: onAddMedia }
 *
 * Exactly the fields `VerbRail` reads, and no outcome line: rails have no room for one, and an
 * earlier version returned it anyway, so every rail item carried a tooltip sentence nothing
 * would ever render.
 *
 * All three builders here are generic over the id so the literal survives: callers assign into
 * arrays typed with narrower unions (`LibraryMenuAction`, `MediaMenuAction`), and a plain
 * `VerbId` return would widen `id` and fail those. It's also what makes a typo'd id a compile
 * error at the call site.
 */
export const verbItem = <T extends VerbId>(id: T) => ({
  id,
  label: VERBS[id].label,
  icon: VERBS[id].icon,
  accent: VERBS[id].defaultAccent,
});

/**
 * The identity half of a labelled pill — `verbItem` plus the outcome line those surfaces show
 * as a tooltip, since their label already names the action.
 *
 * `scoped` picks the variant: `true` for a page that IS one library ("this library"), `false`
 * for a page that is every library ("a library"). Only verbs that have an outcome may be passed,
 * enforced by the `T` constraint — so a labelled pill can't silently end up with no tooltip.
 */
export const verbPillItem = <T extends VerbsWithOutcome>(id: T, opts: { scoped: boolean }) => ({
  ...verbItem(id),
  outcome: opts.scoped ? VERBS[id].outcome!.scoped : VERBS[id].outcome!.unscoped,
});

/**
 * The identity half of an `ActionDrawerItem` (kebab menus, mobile drawers). Same as `verbItem`
 * but with the menu-sized glyph and no outcome line.
 */
export const verbMenuItem = <T extends VerbId>(id: T) => ({
  id,
  label: VERBS[id].label,
  icon: verbMenuIcon(id),
});

/**
 * Just the menu-sized glyph.
 *
 * Five surfaces want only the icon and were reaching through `verbMenuItem(id).icon`, building
 * an `{ id, label }` they immediately discarded — and one of them then read the label back off
 * `VERBS` on the next line, reassembling by hand what the helper had just built.
 */
export const verbMenuIcon = (id: VerbId): ReactNode => smallOf(VERBS[id].icon);

/**
 * Why an inert control is inert, for `VerbRailItem.disabledReason`.
 *
 * **The label leads, always, and this function is what guarantees it.** `VerbRail` swaps
 * `disabledReason` in for BOTH the tooltip and the `aria-label` when an item is disabled — so a
 * reason that didn't name the action would leave a sighted user with an explanation attached to
 * a glyph whose name just disappeared, and a screen reader announcing a sentence with no idea
 * which button it belongs to. Written out per string that was a convention a new one could
 * quietly break, with no compile error and no symptom on the author's screen; built here it
 * can't be got wrong.
 *
 * `why` never says "unavailable" or "disabled": the button is visibly dimmed AND carries the
 * `disabled` attribute, so the state is already stated twice. These add only the reason.
 *
 * Nothing apologises or says "please wait". Some of these controls turn themselves back on and
 * some never will; either way there's nothing to be sorry about.
 */
const verbReason = (label: string, why: string): string => `${label} — ${why}`;

/**
 * Why something is unavailable while its media is still being processed.
 *
 * Says when it comes back, not just that it's gone — the control turns itself on, so the useful
 * fact is the condition. The string this replaced ("Thank you for your patience as we index this
 * media so that you can use this feature") named neither the action nor the outcome.
 *
 * "Processing" is the word the product already uses — `getStatusLabel` renders "Processing (X%)"
 * / "Processing media", and on a mid-processing card that pill sits on the same card as this
 * tooltip. "Indexing" was internal vocabulary leaking out.
 *
 * Takes a label rather than a `VerbId` because one caller's verb isn't in the catalog:
 * `MomentModal`'s agent verb is scoped to a clip, not a media item, and still needs this exact
 * sentence.
 */
export const processingReason = (label: string): string =>
  verbReason(label, 'available once this media finishes processing.');

/**
 * Why something is unavailable on media whose processing failed, and on an upload the user
 * stopped. Both are terminal — unlike `processingReason` there is no condition to state, so
 * these name the fact and stop.
 *
 * These exist because "say nothing, the Retry button beside it explains the state" turned out to
 * be false on two of the surfaces that render these rails: the search-result moment preview has
 * no kebab at all, and `cancelled` is never offered a retry anywhere (only `errored` is). Both
 * left a dimmed control whose tooltip was its own name. Whether a neighbouring control happens
 * to explain the state is not something this file can know, so it no longer tries — a reason is
 * always produced, and a surface that also shows Retry is merely saying it twice.
 */
export const processingFailedReason = (label: string): string =>
  verbReason(label, 'this media failed to process.');

export const uploadCancelledReason = (label: string): string =>
  verbReason(label, 'this upload was cancelled.');

/**
 * Why a library verb is unavailable on a library with nothing in it.
 *
 * Says what to DO, not just what's wrong, where there's something to do: "this library is empty"
 * is a diagnosis the user can already see from the row; "add media to it first" is the sentence
 * that ends the interaction.
 *
 * `canAddMedia` is not decoration. `buildLibraryRailItems` dims Add media on a read-only library,
 * so on a read-only empty library the Add media button beside this one is itself inert — telling
 * that user to add media is an instruction they cannot follow. There, the bare fact is the honest
 * answer.
 *
 * Both forms differ from `emptyLibraryDisabledReason` in `LibrarySwitch` on purpose: that one
 * fills a picker row's stats slot, where "This library is empty." is the whole sentence and
 * there's no label to lead with.
 */
export const verbEmptyLibraryReason = (id: VerbId, opts: { canAddMedia: boolean }): string =>
  verbReason(
    VERBS[id].label,
    opts.canAddMedia ? 'add media to this library first.' : 'this library is empty.',
  );

/**
 * Why clipping is unavailable on a still image.
 *
 * The verb is offered-but-inert rather than absent: a control that silently vanishes on some
 * media reads as a bug, and the user has nothing to ask. Naming the reason costs one tooltip.
 *
 * Says what's missing (audio) rather than restating the input ("this is an image"), so it
 * explains the rule instead of the rejection. This is the only "can't clip" state we can detect
 * — `isProbablyImage` reads `frameCount === 1`, and nothing in the metadata reports whether a
 * video carries an audio track, so a silent video is not a state this can honestly describe.
 */
export const IMAGE_CANNOT_CLIP_REASON = verbReason(
  VERBS['clip-podcast'].label,
  'an image has no audio to clip.',
);

/**
 * Why Add media is unavailable on a library the user can't write to.
 *
 * Dimmed rather than hidden, for the same reason as the clip verb on an image. This one is a
 * permission rather than a "not yet", so there is no recovery to offer — naming the constraint
 * IS the whole answer.
 */
export const READ_ONLY_LIBRARY_REASON = verbReason(
  VERBS['add-media'].label,
  'this library is read-only.',
);
